import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./DateRangeFilter.scss";

/** Standard quick-select for the rest of the product (includes All Time; no 12-month cap). */
export const DEFAULT_PRESETS = [
  "Today",
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
  "Yesterday",
  "Last Week",
  "Last Month",
  "Last Year",
  "All Time",
  "Custom",
];

/** Future presets — only View appointments + Analytics. */
export const FUTURE_PRESETS = [
  "Tomorrow",
  "Next Week",
  "Next Month",
  "Next Quarter",
  "Next Year",
];

/** E-Planner base list: no All Time, no future. */
export const EPLANNER_BASE_PRESETS = [
  "Today",
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
  "Yesterday",
  "Last Week",
  "Last Month",
  "Last Quarter",
  "Last Year",
  "Custom",
];

/** View appointments / Analytics: base + future (still no All Time). */
export const EPLANNER_DATE_PRESETS = [
  "Today",
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
  "Yesterday",
  "Last Week",
  "Last Month",
  "Last Quarter",
  "Last Year",
  ...FUTURE_PRESETS,
  "Custom",
];

const DAY_LABELS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const LEGACY_PRESET_ALIASES = {
  "Previous Week": "Last Week",
  "Previous Month": "Last Month",
  "Previous Quarter": "Last Quarter",
  "Previous Year": "Last Year",
  "Custom Range": "Custom",
};

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function endOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
}

function toIsoDate(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseIsoDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return startOfDay(new Date(year, month - 1, day));
}

function formatDisplayDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function sameDay(a, b) {
  return Boolean(a && b && toIsoDate(a) === toIsoDate(b));
}

/** Map legacy "Previous …" / Custom Range labels to current names. */
export function normalizePreset(preset) {
  const raw = String(preset || "").trim();
  return LEGACY_PRESET_ALIASES[raw] || raw;
}

function maxCustomEnd(startDate, maxMonths) {
  return startOfDay(addMonths(startOfDay(startDate), maxMonths));
}

function isCustomRangeWithinLimit(startDate, endDate, maxMonths) {
  if (!maxMonths || !startDate || !endDate) return true;
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);
  return end.getTime() <= maxCustomEnd(start, maxMonths).getTime();
}

function observedHolidayDate(date) {
  const day = date.getDay();
  if (day === 6) return addDays(date, 2);
  if (day === 0) return addDays(date, 1);
  return date;
}

function firstMonday(year, month) {
  const date = new Date(year, month, 1);
  while (date.getDay() !== 1) {
    date.setDate(date.getDate() + 1);
  }
  return date;
}

function lastMonday(year, month) {
  const date = new Date(year, month + 1, 0);
  while (date.getDay() !== 1) {
    date.setDate(date.getDate() - 1);
  }
  return date;
}

function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function getUkHolidaysForYear(year) {
  const easter = getEasterSunday(year);
  const christmas = new Date(year, 11, 25);
  const boxingDay = new Date(year, 11, 26);
  const observedChristmas = observedHolidayDate(christmas);
  const observedBoxingDay =
    toIsoDate(observedChristmas) === toIsoDate(observedHolidayDate(boxingDay))
      ? addDays(observedHolidayDate(boxingDay), 1)
      : observedHolidayDate(boxingDay);

  return [
    { date: observedHolidayDate(new Date(year, 0, 1)), name: "New Year's Day" },
    { date: addDays(easter, -2), name: "Good Friday" },
    { date: addDays(easter, 1), name: "Easter Monday" },
    { date: firstMonday(year, 4), name: "Early May Bank Holiday" },
    { date: lastMonday(year, 4), name: "Spring Bank Holiday" },
    { date: lastMonday(year, 7), name: "Summer Bank Holiday" },
    { date: observedChristmas, name: "Christmas Day" },
    { date: observedBoxingDay, name: "Boxing Day" },
  ];
}

function getHolidaysBetween(startDate, endDate, countryCode) {
  if (!startDate || !endDate || String(countryCode) !== "+44") return [];
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  const years = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    years.push(year);
  }

  return years
    .flatMap(getUkHolidaysForYear)
    .filter((holiday) => holiday.date >= start && holiday.date <= end)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function getPresetRange(preset) {
  const key = normalizePreset(preset);
  const today = startOfDay(new Date());
  const yesterday = addDays(today, -1);
  const tomorrow = addDays(today, 1);
  const thisWeekStart = addDays(today, -today.getDay());
  const previousWeekStart = addDays(thisWeekStart, -7);
  const nextWeekStart = addDays(thisWeekStart, 7);
  const thisMonthStart = startOfMonth(today);
  const previousMonth = addMonths(today, -1);
  const nextMonth = addMonths(today, 1);
  const thisQuarterStart = startOfQuarter(today);
  const previousQuarterDate = addMonths(thisQuarterStart, -3);
  const nextQuarterDate = addMonths(thisQuarterStart, 3);

  switch (key) {
    case "Today":
      return { startDate: today, endDate: today };
    case "Yesterday":
      return { startDate: yesterday, endDate: yesterday };
    case "Tomorrow":
      return { startDate: tomorrow, endDate: tomorrow };
    case "This Week":
      return { startDate: thisWeekStart, endDate: today };
    case "Last Week":
    case "Previous Week":
      return {
        startDate: previousWeekStart,
        endDate: addDays(thisWeekStart, -1),
      };
    case "Next Week":
      return {
        startDate: nextWeekStart,
        endDate: addDays(nextWeekStart, 6),
      };
    case "Last 7 Days":
      return { startDate: addDays(today, -7), endDate: today };
    case "Last 15 Days":
      return { startDate: addDays(today, -15), endDate: today };
    case "Last 30 Days":
      return { startDate: addDays(today, -30), endDate: today };
    case "Last 90 Days":
      return { startDate: addDays(today, -90), endDate: today };
    case "This Month":
      return { startDate: thisMonthStart, endDate: today };
    case "Last Month":
    case "Previous Month":
      return {
        startDate: startOfMonth(previousMonth),
        endDate: endOfMonth(previousMonth),
      };
    case "Next Month":
      return {
        startDate: startOfMonth(nextMonth),
        endDate: endOfMonth(nextMonth),
      };
    case "This Quarter":
      return { startDate: thisQuarterStart, endDate: today };
    case "Last Quarter":
    case "Previous Quarter":
      return {
        startDate: startOfQuarter(previousQuarterDate),
        endDate: endOfQuarter(previousQuarterDate),
      };
    case "Next Quarter":
      return {
        startDate: startOfQuarter(nextQuarterDate),
        endDate: endOfQuarter(nextQuarterDate),
      };
    case "This Year":
      return { startDate: new Date(today.getFullYear(), 0, 1), endDate: today };
    case "Last Year":
    case "Previous Year":
      return {
        startDate: new Date(today.getFullYear() - 1, 0, 1),
        endDate: new Date(today.getFullYear() - 1, 11, 31),
      };
    case "Next Year":
      return {
        startDate: new Date(today.getFullYear() + 1, 0, 1),
        endDate: new Date(today.getFullYear() + 1, 11, 31),
      };
    case "All Time":
      return { startDate: null, endDate: null };
    default:
      return { startDate: null, endDate: null };
  }
}

function getCalendarCells(monthDate) {
  const first = startOfMonth(monthDate);
  const firstCell = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
}

function buildYearOptions(centerYear = new Date().getFullYear()) {
  const years = [];
  for (let year = centerYear - 20; year <= centerYear + 5; year += 1) {
    years.push(year);
  }
  return years;
}

function DateMonth({
  monthDate,
  draftStart,
  draftEnd,
  onSelectDate,
  holidayDates,
  onMonthYearChange,
  pickingEnd,
  panelLabel,
  panel = "start",
}) {
  const cells = getCalendarCells(monthDate);
  const month = monthDate.getMonth();
  const year = monthDate.getFullYear();
  const yearOptions = useMemo(() => buildYearOptions(year), [year]);

  return (
    <div className="aq-date-range-modal__month">
      {panelLabel ? (
        <div className="aq-date-range-modal__month-label">{panelLabel}</div>
      ) : null}
      {onMonthYearChange ? (
        <div className="aq-date-range-modal__month-pickers">
          <button
            type="button"
            className="aq-date-range-modal__month-step"
            aria-label="Previous month"
            onClick={() =>
              onMonthYearChange(addMonths(new Date(year, month, 1), -1))
            }
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <label className="aq-date-range-modal__month-picker">
            <span className="visually-hidden">Month</span>
            <select
              value={month}
              aria-label="Month"
              onChange={(event) => {
                const nextMonth = Number(event.target.value);
                onMonthYearChange(new Date(year, nextMonth, 1));
              }}
            >
              {MONTH_LABELS.map((label, index) => (
                <option key={label} value={index}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="aq-date-range-modal__month-picker aq-date-range-modal__month-picker--year">
            <span className="visually-hidden">Year</span>
            <select
              value={year}
              aria-label="Year"
              onChange={(event) => {
                const nextYear = Number(event.target.value);
                onMonthYearChange(new Date(nextYear, month, 1));
              }}
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>
                  {optionYear}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="aq-date-range-modal__month-step"
            aria-label="Next month"
            onClick={() =>
              onMonthYearChange(addMonths(new Date(year, month, 1), 1))
            }
          >
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <h3>{`${MONTH_LABELS[month]} ${year}`}</h3>
      )}
      <div className="aq-date-range-modal__weekdays">
        {DAY_LABELS.map((label, index) => (
          <span
            key={label}
            className={index === 0 || index === 6 ? "is-weekend" : ""}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="aq-date-range-modal__calendar">
        {cells.map((date) => {
          const inMonth = date.getMonth() === month;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const isHoliday = holidayDates.has(toIsoDate(date));
          const selectedStart = sameDay(date, draftStart);
          const selectedEnd = sameDay(date, draftEnd);
          const inRange =
            draftStart &&
            draftEnd &&
            startOfDay(date).getTime() >= startOfDay(draftStart).getTime() &&
            endOfDay(date).getTime() <= endOfDay(draftEnd).getTime();
          const isHoverTarget =
            pickingEnd &&
            draftStart &&
            !draftEnd &&
            inMonth &&
            startOfDay(date).getTime() >= startOfDay(draftStart).getTime();

          return (
            <button
              type="button"
              key={toIsoDate(date)}
              className={[
                "aq-date-range-modal__day",
                inMonth ? "" : "is-muted",
                isWeekend ? "is-weekend" : "",
                isHoliday ? "is-holiday" : "",
                inRange ? "is-in-range" : "",
                selectedStart ? "is-selected is-start" : "",
                selectedEnd ? "is-selected is-end" : "",
                isHoverTarget ? "is-pending-end" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelectDate(date, panel)}
            >
              <span className="aq-date-range-modal__day-num">
                {date.getDate()}
              </span>
              {isHoliday && inMonth ? (
                <span className="aq-date-range-modal__day-dot" aria-hidden="true" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function initialRightMonth(leftMonth, endDate) {
  const left = startOfMonth(leftMonth || new Date());
  if (endDate) {
    const endMonth = startOfMonth(endDate);
    if (endMonth.getTime() !== left.getTime()) return endMonth;
  }
  return addMonths(left, 1);
}

/**
 * @param {object} props
 * @param {boolean} [props.eplannerModule] E-Planner screens: no All Time; default 12-month custom cap.
 * @param {boolean} [props.includeFuturePresets] View appointments / Analytics — Tomorrow / Next …
 * @param {number|null} [props.maxCustomMonths] Cap custom range. Null/0 = no limit (ignored default 12 when eplannerModule).
 * @param {string[]} [props.presetOptions] Override quick-select list entirely.
 */
const DateRangeFilter = ({
  value,
  onChange,
  placeholder = "Select date range",
  countryCode = "+44",
  presetOptions,
  eplannerModule = false,
  includeFuturePresets = false,
  maxCustomMonths,
  compact = false,
  hideTrigger = false,
  open: openProp,
  onOpenChange,
  disabled = false,
}) => {
  const customMonthLimit = (() => {
    if (maxCustomMonths === null || maxCustomMonths === 0) return null;
    if (Number(maxCustomMonths) > 0) return Number(maxCustomMonths);
    if (eplannerModule) return 12;
    return null;
  })();
  const customLimitMessage = customMonthLimit
    ? `You can select a maximum of ${customMonthLimit} months. Please choose a shorter range.`
    : "";

  const resolvedPresets = useMemo(() => {
    if (Array.isArray(presetOptions) && presetOptions.length) {
      return presetOptions.map(normalizePreset);
    }
    if (includeFuturePresets) return EPLANNER_DATE_PRESETS;
    if (eplannerModule) return EPLANNER_BASE_PRESETS;
    return DEFAULT_PRESETS;
  }, [eplannerModule, includeFuturePresets, presetOptions]);

  const selectedPreset = normalizePreset(
    value?.preset || value?.dateRange || "Custom",
  );
  const selectedStartValue = value?.startDate || value?.dateFrom || "";
  const selectedEndValue = value?.endDate || value?.dateTo || "";
  const selectedStart = parseIsoDate(selectedStartValue);
  const selectedEnd = parseIsoDate(selectedEndValue);
  const presetRange = useMemo(
    () => getPresetRange(selectedPreset),
    [selectedPreset],
  );
  const effectiveStart = selectedStart || presetRange.startDate;
  const effectiveEnd = selectedEnd || presetRange.endDate;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [activePreset, setActivePreset] = useState(selectedPreset);
  const [draftStart, setDraftStart] = useState(effectiveStart);
  const [draftEnd, setDraftEnd] = useState(effectiveEnd);
  const [leftViewMonth, setLeftViewMonth] = useState(
    startOfMonth(effectiveStart || new Date()),
  );
  const [rightViewMonth, setRightViewMonth] = useState(() =>
    initialRightMonth(
      startOfMonth(effectiveStart || new Date()),
      effectiveEnd,
    ),
  );
  const [customLimitHint, setCustomLimitHint] = useState("");
  const selectedHolidays = useMemo(
    () => getHolidaysBetween(draftStart, draftEnd, countryCode),
    [countryCode, draftEnd, draftStart],
  );
  const holidayDates = useMemo(
    () => new Set(selectedHolidays.map((holiday) => toIsoDate(holiday.date))),
    [selectedHolidays],
  );

  useEffect(() => {
    if (!open) return;
    let preset = selectedPreset;
    if (
      (eplannerModule || includeFuturePresets) &&
      normalizePreset(preset) === "All Time"
    ) {
      preset = "This Month";
    }
    const range = getPresetRange(preset);
    const nextStart = parseIsoDate(selectedStartValue) || range.startDate;
    const nextEnd = parseIsoDate(selectedEndValue) || range.endDate;
    const tooLong =
      Boolean(customMonthLimit) &&
      normalizePreset(preset) === "Custom" &&
      nextStart &&
      nextEnd &&
      !isCustomRangeWithinLimit(nextStart, nextEnd, customMonthLimit);
    setActivePreset(preset || "Custom");
    setDraftStart(nextStart);
    setDraftEnd(nextEnd);
    const left = startOfMonth(nextStart || new Date());
    setLeftViewMonth(left);
    setRightViewMonth(initialRightMonth(left, nextEnd));
    setCustomLimitHint(tooLong ? customLimitMessage : "");
  }, [
    customLimitMessage,
    customMonthLimit,
    eplannerModule,
    includeFuturePresets,
    open,
    selectedEndValue,
    selectedPreset,
    selectedStartValue,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const displayValue =
    selectedPreset === "All Time"
      ? "All Time"
      : effectiveStart && effectiveEnd
        ? `${formatDisplayDate(effectiveStart)} - ${formatDisplayDate(effectiveEnd)}`
        : placeholder;

  const selectPreset = (preset) => {
    const next = normalizePreset(preset);
    setActivePreset(next);
    setCustomLimitHint("");
    if (next === "All Time") {
      setDraftStart(null);
      setDraftEnd(null);
      return;
    }
    if (next === "Custom") return;
    const range = getPresetRange(next);
    setDraftStart(range.startDate);
    setDraftEnd(range.endDate);
    const left = startOfMonth(range.startDate || new Date());
    setLeftViewMonth(left);
    setRightViewMonth(initialRightMonth(left, range.endDate));
  };

  const applyDraftRange = (start, end) => {
    setDraftStart(start);
    setDraftEnd(end);
    if (
      customMonthLimit &&
      start &&
      end &&
      !isCustomRangeWithinLimit(start, end, customMonthLimit)
    ) {
      setCustomLimitHint(customLimitMessage);
    } else {
      setCustomLimitHint("");
    }
  };

  // Start panel edits from-date; End panel edits to-date.
  // When only start is set, a later click on Start can still complete the range.
  const selectDate = (date, panel = "start") => {
    const picked = startOfDay(date);
    setActivePreset("Custom");

    if (panel === "end") {
      if (!draftStart) {
        applyDraftRange(picked, null);
        setLeftViewMonth(startOfMonth(picked));
        return;
      }
      if (picked.getTime() < draftStart.getTime()) {
        applyDraftRange(picked, draftStart);
        setLeftViewMonth(startOfMonth(picked));
        setRightViewMonth(startOfMonth(draftStart));
        return;
      }
      applyDraftRange(draftStart, picked);
      setRightViewMonth(startOfMonth(picked));
      return;
    }

    // Start panel — if awaiting end, allow second click to finish the range
    if (draftStart && !draftEnd) {
      if (picked.getTime() < draftStart.getTime()) {
        applyDraftRange(picked, null);
        setLeftViewMonth(startOfMonth(picked));
        return;
      }
      applyDraftRange(draftStart, picked);
      setRightViewMonth(startOfMonth(picked));
      return;
    }

    // No start yet, or both already set → change start; keep end when still valid
    if (draftEnd && picked.getTime() > draftEnd.getTime()) {
      applyDraftRange(picked, null);
      setLeftViewMonth(startOfMonth(picked));
      return;
    }
    applyDraftRange(picked, draftEnd || null);
    setLeftViewMonth(startOfMonth(picked));
  };

  const customRangeTooLong =
    Boolean(customMonthLimit) &&
    normalizePreset(activePreset) === "Custom" &&
    draftStart &&
    draftEnd &&
    !isCustomRangeWithinLimit(draftStart, draftEnd, customMonthLimit);

  const awaitingEndDate =
    normalizePreset(activePreset) === "Custom" &&
    Boolean(draftStart) &&
    !draftEnd;

  const footerAlert = customRangeTooLong
    ? customLimitHint || customLimitMessage
    : awaitingEndDate
      ? "Select an end date to apply this range."
      : customLimitHint || "";

  const canApply =
    activePreset === "All Time" ||
    (Boolean(draftStart && draftEnd) && !customRangeTooLong);

  const selectionSummary =
    draftStart && draftEnd
      ? `${formatDisplayDate(draftStart)} – ${formatDisplayDate(draftEnd)}`
      : draftStart
        ? `From ${formatDisplayDate(draftStart)} · pick end date`
        : "Pick a start date, then an end date";

  const applyRange = () => {
    if (activePreset === "All Time") {
      onChange({
        preset: "All Time",
        startDate: "",
        endDate: "",
        label: "All Time",
      });
      setOpen(false);
      return;
    }
    if (!draftStart || !draftEnd) return;
    if (
      customMonthLimit &&
      !isCustomRangeWithinLimit(draftStart, draftEnd, customMonthLimit)
    ) {
      setCustomLimitHint(customLimitMessage);
      return;
    }

    const start = startOfDay(draftStart);
    const end = startOfDay(draftEnd);
    const preset = normalizePreset(activePreset);

    onChange({
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`,
    });
    setOpen(false);
  };

  return (
    <div
      className={`aq-date-range-filter${hideTrigger ? " aq-date-range-filter--ghost" : ""}`}
    >
      {!hideTrigger ? (
        <button
          type="button"
          className={
            compact
              ? "aq-date-range-filter__input aq-date-range-filter__input--compact"
              : "form-control form-control-sm aq-date-range-filter__input"
          }
          onClick={() => {
            if (disabled) return;
            setOpen(true);
          }}
          disabled={disabled}
          aria-disabled={disabled || undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span>{displayValue}</span>
          <i className="fas fa-calendar-alt" aria-hidden="true" />
        </button>
      ) : null}

      {open &&
        createPortal(
        <div className="aq-date-range-modal" role="presentation">
          <button
            type="button"
            className="aq-date-range-modal__backdrop"
            aria-label="Close date range selector"
            onClick={() => setOpen(false)}
          />
          <div
            className="aq-date-range-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aq-date-range-modal-title"
          >
            <div className="aq-date-range-modal__header">
              <h2 id="aq-date-range-modal-title">Select Date Range</h2>
              <button
                type="button"
                className="aq-date-range-modal__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="aq-date-range-modal__content">
              <aside className="aq-date-range-modal__presets">
                <span>Quick Select</span>
                <div className="aq-date-range-modal__preset-list">
                  {resolvedPresets.map((preset) => (
                    <button
                      type="button"
                      key={preset}
                      className={
                        normalizePreset(activePreset) ===
                        normalizePreset(preset)
                          ? "is-active"
                          : ""
                      }
                      onClick={() => selectPreset(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </aside>

              <section className="aq-date-range-modal__main">
                <div className="aq-date-range-modal__selection-bar">
                  <span>{selectionSummary}</span>
                </div>

                <div className="aq-date-range-modal__months">
                  <DateMonth
                    panelLabel="Start"
                    panel="start"
                    monthDate={leftViewMonth}
                    draftStart={draftStart}
                    draftEnd={draftEnd}
                    onSelectDate={selectDate}
                    holidayDates={holidayDates}
                    onMonthYearChange={(date) =>
                      setLeftViewMonth(startOfMonth(date))
                    }
                    pickingEnd={awaitingEndDate}
                  />
                  <DateMonth
                    panelLabel="End"
                    panel="end"
                    monthDate={rightViewMonth}
                    draftStart={draftStart}
                    draftEnd={draftEnd}
                    onSelectDate={selectDate}
                    holidayDates={holidayDates}
                    onMonthYearChange={(date) =>
                      setRightViewMonth(startOfMonth(date))
                    }
                    pickingEnd={awaitingEndDate}
                  />
                </div>

                <div className="aq-date-range-modal__holidays">
                  <h4>Holidays</h4>
                  {String(countryCode) !== "+44" ? (
                    <p>No holiday calendar configured for this country.</p>
                  ) : selectedHolidays.length === 0 ? (
                    <p>No UK holidays in selected range.</p>
                  ) : (
                    <ul>
                      {selectedHolidays.map((holiday) => (
                        <li key={`${holiday.name}-${toIsoDate(holiday.date)}`}>
                          <strong className="aq-date-range-modal__holiday-date">
                            {formatDisplayDate(holiday.date)}
                          </strong>
                          <span className="aq-date-range-modal__holiday-name">
                            {holiday.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>

            <div className="aq-date-range-modal__footer">
              {footerAlert ? (
                <p
                  className={
                    customRangeTooLong
                      ? "aq-date-range-modal__alert"
                      : "aq-date-range-modal__alert aq-date-range-modal__alert--info"
                  }
                  role="alert"
                >
                  {footerAlert}
                </p>
              ) : null}
              <div className="aq-date-range-modal__footer-actions">
                <button
                  type="button"
                  className="aq-date-range-modal__cancel"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aq-date-range-modal__apply"
                  onClick={applyRange}
                  disabled={!canApply}
                >
                  Apply Range
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DateRangeFilter;
